"use client"

import * as React from "react"

import { dashboardSectionTitleClassName } from "@/components/dashboard-section-heading"
import { AskLeoComposer } from "@/components/ask-leo-composer"
import { useAskLeo } from "@/components/ask-leo-context"
import {
  LeoAmbientSurface,
  LeoComposerVeil,
} from "@/components/leo-ambient-surface"
import { Button } from "@/components/ui/button"
import { Kbd, KbdGroup } from "@/components/ui/kbd"
import { LeoIcon } from "@/components/ui/leo-icon"
import { StatusBadge } from "@/components/ui/status-badge"
import { useLeoPresence } from "@/hooks/use-leo-presence"
import { cn } from "@/lib/utils"
import {
  LEO_MISSION_STATUS_META,
  type SurveysDashboardLeoInsight,
} from "@/lib/surveys-dashboard-leo-insight"
import {
  surveysOnboardingRailHeading,
  surveysOnboardingVideoEmbedUrl,
  surveysOnboardingVideoPosterUrl,
  surveysOnboardingVideos,
  type SurveysOnboardingRailPhase,
  type SurveysOnboardingVideo,
} from "@/lib/surveys-onboarding-media"

interface EvaluationDashboardOnboardingWelcomeRailProps {
  phase?: SurveysOnboardingRailPhase
  insight?: SurveysDashboardLeoInsight
}

function LeoAssistanceCard({ insight }: { insight: SurveysDashboardLeoInsight }) {
  const { openWithPrompt } = useAskLeo()
  const [message, setMessage] = React.useState("")
  const { present: leoPresent, presenceHandlers } = useLeoPresence(false)

  React.useEffect(() => {
    setMessage("")
  }, [insight.askLeoPrompt])

  const openLeoWindow = React.useCallback(
    (prompt: string) => {
      openWithPrompt(prompt, { dock: "window" })
    },
    [openWithPrompt],
  )

  const mission = LEO_MISSION_STATUS_META[insight.missionStatus]

  return (
    <section
      aria-labelledby="surveys-dashboard-leo-assistance-heading"
      className="overflow-hidden rounded-xl border border-border"
      style={
        {
          "--leo-thread-surface": "var(--background)",
        } as React.CSSProperties
      }
    >
      <LeoAmbientSurface
        active
        thinking={false}
        hasThread={false}
        idleAnchorY={72}
        className="h-auto flex-none"
      >
        <div className="relative z-[1] flex flex-col gap-3 p-3" {...presenceHandlers}>
          <div className="flex min-w-0 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <LeoIcon variant="interactive" size="sm" motionLive={leoPresent} />
              <h3
                id="surveys-dashboard-leo-assistance-heading"
                className="font-heading m-0 truncate text-lg font-semibold leading-tight tracking-tight text-foreground"
              >
                Leo assistance
              </h3>
            </div>
            <StatusBadge
              label={mission.label}
              tone={mission.tone}
              size="md"
              icon={mission.icon}
            />
          </div>
          <p className="min-w-0 text-sm font-medium text-foreground">{insight.title}</p>
          <ol className="m-0 flex list-none flex-col gap-1.5 p-0" aria-label="Leo plan">
            {insight.steps.map(step => (
              <li key={step.id} className="flex min-h-6 items-start gap-2 text-sm">
                <i
                  className={cn(
                    "fa-light mt-0.5 shrink-0 text-xs",
                    step.done ? "fa-circle-check text-muted-foreground" : "fa-circle text-muted-foreground",
                  )}
                  aria-hidden
                />
                <span className={step.done ? "text-muted-foreground" : "text-foreground"}>
                  <span className="sr-only">{step.done ? "Done. " : "Not started. "}</span>
                  {step.label}
                </span>
              </li>
            ))}
          </ol>
          <div className="relative">
            <LeoComposerVeil />
            <div className="relative flex flex-col gap-2.5">
              <Button
                type="button"
                className="w-full"
                onClick={() => openLeoWindow(insight.askLeoPrompt)}
              >
                Review plan
                <KbdGroup className="ms-1.5">
                  <Kbd variant="bare">⏎</Kbd>
                </KbdGroup>
              </Button>
              <AskLeoComposer
                value={message}
                onChange={setMessage}
                onSubmit={openLeoWindow}
                shellMaxWidth="full"
                placeholder="Ask Leo anything…"
                inputLabel="Ask Leo"
                submitButtonAriaLabel="Send to Leo"
                composerShellClassName="min-h-8 p-0 shadow-xs"
              />
            </div>
          </div>
        </div>
      </LeoAmbientSurface>
    </section>
  )
}

export function EvaluationDashboardOnboardingWelcomeRail({
  phase = "setup",
  insight,
}: EvaluationDashboardOnboardingWelcomeRailProps) {
  const videos = surveysOnboardingVideos(phase)
  const heading = surveysOnboardingRailHeading(phase)
  const [selectedId, setSelectedId] = React.useState(videos[0]?.id ?? "")
  const [videoOpen, setVideoOpen] = React.useState(false)

  React.useEffect(() => {
    setSelectedId(videos[0]?.id ?? "")
    setVideoOpen(false)
  }, [phase])

  const selected = videos.find(video => video.id === selectedId) ?? videos[0]

  if (!selected) return null

  const playVideo = (video: SurveysOnboardingVideo) => {
    setSelectedId(video.id)
    setVideoOpen(true)
  }

  return (
    <aside className="flex w-full min-w-0 flex-col gap-3">
      <section aria-labelledby="surveys-onboarding-welcome-heading" className="flex shrink-0 flex-col gap-3">
        <h3
          id="surveys-onboarding-welcome-heading"
          className={dashboardSectionTitleClassName}
        >
          {heading}
        </h3>
        <div className="relative aspect-video overflow-hidden rounded-xl border border-border bg-muted">
          {videoOpen ? (
            <iframe
              key={selected.id}
              title={selected.title}
              className="size-full border-0"
              src={surveysOnboardingVideoEmbedUrl(selected.id, true)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <img
                src={surveysOnboardingVideoPosterUrl(selected.id)}
                alt=""
                className="size-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              <Button
                type="button"
                size="lg"
                className="absolute bottom-4 left-4 min-h-11 gap-2 shadow-md"
                onClick={() => playVideo(selected)}
                aria-label={`Play ${selected.title}`}
              >
                <i className="fa-solid fa-play text-xs" aria-hidden="true" />
                Watch
              </Button>
            </>
          )}
        </div>
        {videoOpen ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setVideoOpen(false)}>
            Close video
          </Button>
        ) : null}
      </section>

      <section aria-labelledby="surveys-onboarding-more-videos-heading" className="flex shrink-0 flex-col gap-2">
        <h3 id="surveys-onboarding-more-videos-heading" className="text-sm font-medium text-foreground">
          More videos
        </h3>
        <ul className="flex flex-col gap-1">
          {videos.map(video => {
            const isCurrent = video.id === selected.id
            return (
              <li key={video.id}>
                <button
                  type="button"
                  aria-current={isCurrent ? "true" : undefined}
                  onClick={() => playVideo(video)}
                  className={cn(
                    "flex min-h-11 w-full items-center gap-3 rounded-lg p-1.5 text-left",
                    "hover:bg-interactive-hover-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isCurrent && "bg-interactive-hover-soft",
                  )}
                >
                  <span className="relative w-[5.5rem] shrink-0 overflow-hidden rounded-md border border-border bg-muted aspect-video">
                    <img
                      src={surveysOnboardingVideoPosterUrl(video.id, "mq")}
                      alt=""
                      className="size-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                      <span className="flex size-6 items-center justify-center rounded-full bg-background/80 text-foreground">
                        <i className="fa-solid fa-play text-xs" />
                      </span>
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">{video.title}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{video.coaching}</span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </section>

      {insight ? <LeoAssistanceCard insight={insight} /> : null}
    </aside>
  )
}
